import { useId } from "react";
import { Defs, LinearGradient, Stop } from "react-native-svg";
import type { LucideIcon } from "lucide-react-native";

export type GradientVariant =
  "ember" | "mahogany" | "copper" | "amber" | "bronze";

type GradientStop = { offset: string; color: string };

const VARIANTS: Record<GradientVariant, GradientStop[]> = {
  // Deep brown — mirrors the ScreenTitle text gradient. Default.
  ember: [
    { offset: "0", color: "#3B1A08" },
    { offset: "0.35", color: "#7A3E15" },
    { offset: "0.6", color: "#3B1A08" },
    { offset: "0.85", color: "#6B3410" },
    { offset: "1", color: "#3B1A08" },
  ],
  // Rich dark brown with a subtle red lift — "foundational" feel.
  mahogany: [
    { offset: "0", color: "#2A0F04" },
    { offset: "0.4", color: "#5C2010" },
    { offset: "0.7", color: "#3B1A08" },
    { offset: "1", color: "#2A0F04" },
  ],
  // Lighter, warmer copper — established, professional.
  copper: [
    { offset: "0", color: "#5C2A12" },
    { offset: "0.4", color: "#A8623A" },
    { offset: "0.7", color: "#7A3E15" },
    { offset: "1", color: "#5C2A12" },
  ],
  // Warm gold-brown — suits "money" categories.
  amber: [
    { offset: "0", color: "#6B3410" },
    { offset: "0.4", color: "#C68B45" },
    { offset: "0.7", color: "#8F5A20" },
    { offset: "1", color: "#6B3410" },
  ],
  // Copper-red highlight — suits "gift / celebration" categories.
  bronze: [
    { offset: "0", color: "#4A1408" },
    { offset: "0.4", color: "#B05028" },
    { offset: "0.7", color: "#6B2412" },
    { offset: "1", color: "#4A1408" },
  ],
};

type Props = {
  icon: LucideIcon;
  size?: number;
  strokeWidth?: number;
  /** Color flavor. All variants stay within the warm-brown family. */
  variant?: GradientVariant;
};

/**
 * Brand brown gradient applied to a Lucide icon's stroke.
 * Five variants share a warm-brown palette so groups of icons read as
 * a related family without flattening to a single hue.
 */
export function GradientIcon({
  icon: Icon,
  size = 22,
  strokeWidth = 1.5,
  variant = "ember",
}: Props) {
  const rawId = useId();
  const gradientId = `ember-grad-${variant}-${rawId.replace(/:/g, "")}`;
  const stops = VARIANTS[variant];

  return (
    <Icon size={size} strokeWidth={strokeWidth} color={`url(#${gradientId})`}>
      <Defs>
        <LinearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          {stops.map((s) => (
            <Stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </LinearGradient>
      </Defs>
    </Icon>
  );
}

export default GradientIcon;
